import { BOUGHT_CAP, FREEZE_PRICE } from 'components/Streaks/BuyFreezes/payment';

import {
  formatDayKey,
  formatTimeLeft,
  msUntilCentralMidnight,
  previousDayKey
} from './centralTime';
import {
  avatarsLabel,
  avatarSlots,
  currentMilestone,
  FlameStage,
  flameStageFor,
  frameTierFor,
  ladderFillSteps,
  MilestoneRewards,
  nextMilestone,
  sortedMilestones,
  StatusAvatarLookup,
  withRewards
} from './milestones';
import { FrameTier, StreakSnapshot } from './types';

/**
 * The display states shared by the banner, the panel row and the panel card
 * (canvas boards A, B and C, column for column). Paused is Phase 2.
 */
export type StreakMomentState =
  | 'new'
  | 'open'
  | 'counted'
  | 'lastcall'
  | 'milestone'
  | 'frozen'
  | 'missed'
  | 'restart';

/** Last call starts when this much of the gaming day is left (EN-11155). */
export const LAST_CALL_MS = 2 * 3600 * 1000;

export interface StreakView {
  state: StreakMomentState;
  days: number;
  best: number;
  flops: number;
  flopsNeeded: number;
  freezes: number;
  qualifiedToday: boolean;
  flameStage: FlameStage | 'ember';
  frameTier: FrameTier | null;
  current?: MilestoneRewards;
  next?: MilestoneRewards;
  daysToNext: number;
  milestones: MilestoneRewards[];
  /** "1h 52m" while last call is on. */
  timeLeft: string;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** `statusAvatars` supplies the avatar counts; without it the copy names no count. */
export function deriveStreakView(
  snap: StreakSnapshot,
  nowMs: number,
  statusAvatars?: StatusAvatarLookup
): StreakView {
  const milestones = sortedMilestones(withRewards(snap.milestones, statusAvatars));
  const days = Math.max(0, snap.currentStreak);
  const qualifiedToday = snap.lastQualifiedDay === snap.day;
  const msLeft = msUntilCentralMidnight(nowMs);
  const reachedToday =
    qualifiedToday &&
    !!snap.notice?.milestoneReached &&
    snap.notice.milestoneReached.day === snap.day;
  const endedRecently = snap.status === 'ended' && !!snap.notice?.ended && !qualifiedToday;
  const frozeLastNight =
    !!snap.notice?.freezeUsedFor && snap.notice.freezeUsedFor === previousDayKey(snap.day);

  let state: StreakMomentState;
  if (endedRecently) state = 'missed';
  else if (days === 0) state = 'new';
  else if (reachedToday) state = 'milestone';
  else if (qualifiedToday) state = days === 1 && snap.bestStreak > 1 ? 'restart' : 'counted';
  else if (msLeft <= LAST_CALL_MS) state = 'lastcall';
  else if (frozeLastNight) state = 'frozen';
  else state = 'open';

  const next = nextMilestone(milestones, days);
  return {
    state,
    days,
    best: Math.max(snap.bestStreak, days),
    flops: Math.min(snap.flopsToday, snap.flopsNeeded),
    flopsNeeded: Math.max(1, snap.flopsNeeded),
    freezes: Math.max(0, snap.freezeBalance),
    qualifiedToday,
    flameStage: state === 'missed' ? 'ember' : flameStageFor(milestones, days),
    frameTier: snap.showFrame && snap.status !== 'ended' ? frameTierFor(milestones, days) : null,
    current: state === 'missed' ? undefined : currentMilestone(milestones, days),
    next,
    daysToNext: next ? next.days - days : 0,
    milestones,
    timeLeft: formatTimeLeft(msLeft)
  };
}

/** "2 days to Grinder status", or "Every status earned". */
export function nextStatusLine(v: StreakView): string {
  if (!v.next) return 'Every status earned';
  return `${plural(v.daysToNext, 'day')} to ${v.next.name} status`;
}

export function ruleLine(flopsNeeded: number): string {
  return `See ${plural(flopsNeeded, 'flop')} today`;
}

// ---------------------------------------------------------------------------
// Banner (EN-11155)
// ---------------------------------------------------------------------------

export type BannerStubItem =
  | { kind: 'text'; label: string; value: string; sub: string; tone?: 'alert' }
  | { kind: 'frame'; label: string; tier: FrameTier; sub: string }
  | { kind: 'freeze'; label: string; sub: string }
  | { kind: 'avatars'; label: string; milestone: MilestoneRewards; slots: number; sub: string };

export interface BannerView {
  headline: string;
  /** A clock icon before the headline (last call). */
  clock: boolean;
  /** The meter, or a one-line text with a check. */
  second: { kind: 'meter' } | { kind: 'text'; text: string };
  stub: BannerStubItem[];
  /** The stub word stays put while the rest rotates (milestone day: "Unlocked"). */
  sharedLabel?: string;
  /** The flame flares once when it first renders in this state. */
  flare: boolean;
  /** Screen-reader line for the meter. */
  meterText: string;
}

export function deriveBannerView(v: StreakView): BannerView {
  const { state } = v;
  let headline = nextStatusLine(v);
  if (state === 'new') headline = 'Start your streak';
  if (state === 'lastcall') headline = `${v.timeLeft} left for day ${v.days + 1}`;
  if (state === 'milestone' && v.current) headline = `${v.current.name} status unlocked`;
  if (state === 'frozen') headline = 'Freeze covered yesterday';
  if (state === 'missed') headline = `Your ${v.best}-day streak ended`;

  let second: BannerView['second'] = { kind: 'meter' };
  if (state === 'counted' || state === 'milestone')
    second = { kind: 'text', text: `Day ${v.days} counted` };
  if (state === 'restart') second = { kind: 'text', text: 'Day 1 counted' };

  let stub: BannerStubItem[] = [
    { kind: 'text', label: 'Longest', value: String(v.best), sub: v.best === 1 ? 'Day' : 'Days' }
  ];
  let sharedLabel: string | undefined;
  const sellsNext =
    state === 'new' || state === 'open' || state === 'counted' || state === 'restart';
  if (v.next && sellsNext) {
    const sub = `Day ${v.next.days}`;
    const slots = avatarSlots(v.next);
    stub = [
      { kind: 'frame', label: 'Frame', tier: v.next.frameKey, sub },
      { kind: 'freeze', label: v.next.freezes === 1 ? 'Freeze' : 'Freezes', sub }
    ];
    // No avatar item until the manifest lists the set: no placeholder count.
    if (slots !== null) {
      stub.push({
        kind: 'avatars',
        label: slots === 1 ? (v.next.custom ? 'Custom' : 'Avatar') : 'Avatars',
        milestone: v.next,
        slots,
        sub
      });
    }
  }
  if (state === 'lastcall') {
    stub = [
      v.freezes > 0
        ? { kind: 'text', label: 'Freezes', value: String(v.freezes), sub: 'Ready' }
        : { kind: 'text', label: 'Protect', value: `$${FREEZE_PRICE}`, sub: 'Tonight' }
    ];
  }
  if (state === 'frozen')
    stub = [{ kind: 'text', label: 'Freezes', value: String(v.freezes), sub: 'Left' }];
  if (state === 'milestone' && v.current) {
    const c = v.current;
    const slots = avatarSlots(c);
    sharedLabel = 'Unlocked';
    stub = [
      {
        kind: 'text',
        label: 'Unlocked',
        value: slots === null ? 'New' : String(slots),
        sub: slots === 1 ? 'Avatar' : 'Avatars'
      },
      {
        kind: 'text',
        label: 'Unlocked',
        value: String(c.freezes),
        sub: c.freezes === 1 ? 'Freeze' : 'Freezes'
      },
      { kind: 'text', label: 'Unlocked', value: c.name, sub: 'Frame' }
    ];
  }
  if (!v.next && state !== 'milestone' && state !== 'missed' && state !== 'new') {
    stub = [
      {
        kind: 'text',
        label: 'Status',
        value: v.current ? v.current.name : 'Legend',
        sub: `${v.current ? v.current.days : 365}+ days`
      }
    ];
  }

  return {
    headline,
    clock: state === 'lastcall',
    second,
    stub,
    sharedLabel,
    flare: state === 'counted' || state === 'milestone' || state === 'restart',
    meterText: `${v.flops} of ${v.flopsNeeded} flops seen today`
  };
}

// ---------------------------------------------------------------------------
// Account panel row and card (EN-11162)
// ---------------------------------------------------------------------------

export interface LadderRung {
  key: string;
  name: string;
  daysLabel: string;
  avatarLine: string;
  freezeLine: string;
  stage: FlameStage;
  reached: boolean;
  isNext: boolean;
}

export interface PanelView {
  rowTitle: string;
  rowSub: string;
  rowSubAlert: boolean;
  chip?: { name: string; stage: FlameStage };
  notice?: { kind: 'info' | 'snow'; text: string };
  todayDone: boolean;
  endsText: string;
  endsAlert: boolean;
  nextLine: string;
  nextChip: string;
  ladder: LadderRung[];
  /** The rail's fill in rungs from the first (ladderFillSteps). */
  fillSteps: number;
  /** The rung the rail opens on: the current status, so the next two are in view. */
  railStart: number;
  freezesHeld: string;
  freezeSub: string;
  /** At the cap of 3 bought: the Buy control turns into a quiet "3 of 3 bought" (EN-11162 AC 7). */
  boughtAtCap: boolean;
  buyLabel: string;
  bestLabel?: string;
}

/** Ladder rung line: "6 avatars", "1 + custom"; "Avatars" until the manifest count is known. */
function avatarLine(m: MilestoneRewards): string {
  if (m.avatars === null) return m.custom ? 'Avatars + custom' : 'Avatars';
  return m.custom ? `${m.avatars} + custom` : plural(m.avatars, 'avatar');
}

export function derivePanelView(v: StreakView, snap: StreakSnapshot): PanelView {
  const { state } = v;
  const c = v.current;
  const subs: Record<StreakMomentState, string> = {
    new: ruleLine(v.flopsNeeded),
    open: `Today: ${v.flops} of ${v.flopsNeeded} flops seen`,
    counted: `Day ${v.days} counted · day ${v.days + 1} at midnight`,
    lastcall: `${v.timeLeft} left for day ${v.days + 1} · ${
      v.freezes > 0 ? `${plural(v.freezes, 'freeze')} ready` : 'no freezes'
    }`,
    milestone: c ? `New frame · ${avatarsLabel(c)} · ${plural(c.freezes, 'freeze')}` : '',
    frozen: `A freeze covered yesterday · ${v.freezes} left`,
    missed: `Longest ${v.best} days · start again today`,
    restart: `Day 1 counted · longest ${v.best} days`
  };

  let rowTitle = `${v.days}-day streak`;
  if (state === 'new') rowTitle = 'Start your streak';
  if (state === 'missed') rowTitle = `Streak ended at ${v.best} days`;

  let notice: PanelView['notice'];
  if (state === 'milestone' && c) {
    notice = {
      kind: 'info',
      text:
        `${c.name} status unlocked. Frame on, ${
          c.avatars === null ? 'new avatars' : plural(c.avatars, 'new avatar')
        } to keep` +
        `${c.custom ? ', a custom avatar to request' : ''}, ${plural(
          c.freezes,
          'free freeze'
        )} added.`
    };
  }
  // A streak's first day, new or a restart, came with a free freeze (EN-11162 AC 8).
  if (
    (state === 'counted' || state === 'restart') &&
    snap.notice?.streakStarted?.day === snap.day
  ) {
    notice = {
      kind: 'snow',
      text: 'Day 1 counted, and a free freeze came with it. It covers the first day you miss, by itself.'
    };
  }
  if (state === 'frozen') {
    notice = {
      kind: 'snow',
      text: `A freeze covered yesterday. Still ${v.days} days, ${plural(v.freezes, 'freeze')} left.`
    };
  }
  if (state === 'missed') {
    notice = {
      kind: 'info',
      text: `You missed a day, so the count is back to 0. Your longest stays ${v.best} days.`
    };
  }

  let found = false;
  const ladder: LadderRung[] = v.milestones.map((m) => {
    const reached = state !== 'missed' && v.days >= m.days;
    const isNext = !reached && !found;
    if (isNext) found = true;
    return {
      key: m.key,
      name: m.name,
      daysLabel: `${m.days} days`,
      avatarLine: avatarLine(m),
      freezeLine: `+${plural(m.freezes, 'freeze')}`,
      stage: reached ? flameStageFor(v.milestones, m.days) : 0,
      reached,
      isNext
    };
  });

  let freezeSub = 'Each covers a missed day';
  if (snap.lastFreezeUsedDay) freezeSub = `Last used ${formatDayKey(snap.lastFreezeUsedDay)}`;
  if (state === 'frozen') freezeSub = 'One covered last night';
  const boughtAtCap = snap.promoFreezes + snap.cashFreezes >= BOUGHT_CAP;
  if (boughtAtCap) freezeSub = 'Bought limit reached · use one to buy more';

  return {
    rowTitle,
    rowSub: subs[state],
    rowSubAlert: state === 'lastcall',
    chip:
      c && state !== 'missed'
        ? { name: c.name, stage: flameStageFor(v.milestones, v.days) }
        : undefined,
    notice,
    todayDone: v.qualifiedToday,
    endsText: state === 'lastcall' ? `${v.timeLeft} left` : 'Ends at midnight CT',
    endsAlert: state === 'lastcall',
    nextLine: nextStatusLine(v),
    nextChip: v.next ? `Frame + ${avatarsLabel(v.next)}` : 'Legend',
    ladder,
    fillSteps: state === 'missed' ? 0 : ladderFillSteps(v.milestones, v.days),
    railStart: Math.max(
      0,
      v.milestones.findIndex((m) => m.key === v.current?.key)
    ),
    freezesHeld: v.freezes === 0 ? 'none held' : `${v.freezes} held`,
    freezeSub,
    boughtAtCap,
    buyLabel: boughtAtCap ? `${BOUGHT_CAP} of ${BOUGHT_CAP} bought` : `Buy · $${FREEZE_PRICE} each`,
    bestLabel: v.best > 0 ? `Longest streak · ${plural(v.best, 'day')}` : undefined
  };
}
